import { dontUpsell } from '@trello/browser';
import { Entitlements } from '@trello/entitlements';
import type { Organization } from '@trello/model-types';
import { isProblem, needsCC } from '@trello/paid-account';
import { getOrganizationBillingUrl } from '@trello/urls';

import { useCcpBillingExperience } from 'app/src/components/CcpExperience';
import type { AdminSettingsDropdownProps } from './AdminSettingsDropdown';

interface UseAdminSettingsLinksArgs {
  workspaceId?: string;
  isWorkspaceAdmin: boolean;
  organization?:
    | (Pick<Organization, 'name' | 'offering' | 'premiumFeatures'> & {
        idEntitlement?: string | null;
        paidAccount?: { standing: number | null } | null;
      })
    | null;
}

export const useAdminSettingsLinks = ({
  workspaceId,
  isWorkspaceAdmin,
  organization,
}: UseAdminSettingsLinksArgs): AdminSettingsDropdownProps => {
  const workspaceName = organization?.name;
  const premiumFeatures = organization?.premiumFeatures ?? [];
  const paidAccountStanding = organization?.paidAccount?.standing ?? -1;
  const accountProblem = isProblem(paidAccountStanding);
  const accountDisabled = needsCC(paidAccountStanding);
  const offering = organization?.offering;

  const {
    isCcpBillingEnabled,
    canManageBilling,
    changePlanUrl,
    billingPageUrl,
  } = useCcpBillingExperience({
    premiumFeatures,
    entitlementId: organization?.idEntitlement ?? '',
  });

  // Billing page is visible to workspace admins, but not on desktop where
  // upsells are disabled
  let canViewBillingPage = !dontUpsell() && isWorkspaceAdmin && !!workspaceId;

  // If the workspace has a CCP entitlement, then CCP tells us whether the
  // current user has permission to view the change plan page in the billing console
  if (isCcpBillingEnabled) {
    canViewBillingPage = Boolean(
      !dontUpsell() && !!workspaceId && canManageBilling,
    );
  }

  let billingUrl = getOrganizationBillingUrl(workspaceName);
  if (isCcpBillingEnabled) {
    billingUrl = Entitlements.isFree(offering) ? changePlanUrl : billingPageUrl;
  }

  // Show "upgrade" link for a free workspace unless it is disabled
  const shouldShowUpgrade =
    canViewBillingPage && Entitlements.isFree(offering) && !accountDisabled;

  // Show "billing" link for a Premium or Standard workspace or a disabled
  // workspace but not for Premium PO since it is externally billed
  const isPaidWorkspace =
    (Entitlements.isPremium(offering) || Entitlements.isStandard(offering)) &&
    !premiumFeatures?.includes('externallyBilled');
  const shouldShowBilling =
    canViewBillingPage && (isPaidWorkspace || accountDisabled);

  // The "power-ups" link is visible to workspace admins on any paid plan
  // since only workspace admins on a paid plan have the write permissions
  // to enable/disable power-ups across all boards in the workspace
  const shouldShowPowerups =
    !!workspaceId &&
    isWorkspaceAdmin &&
    !accountDisabled &&
    !Entitlements.isFree(offering);

  const shouldShowExports = premiumFeatures.includes('export');

  return {
    accountProblem,
    workspaceId: workspaceId ?? '',
    workspaceName: workspaceName ?? '',
    shouldShowBilling,
    billingUrl,
    shouldShowExports,
    shouldShowPowerups,
    shouldShowUpgrade,
  };
};
